import React, { useState } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { CameraConfigPanel } from "../manager/CameraConfigPanel";
import { BackupManagerPanel } from "../manager/BackupManagerPanel";
import type { CameraConfig } from "../../services/StorageService";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  cameraConfig: CameraConfig;
  onSaveCameraConfig: (config: CameraConfig) => void;
}

type SettingsTab = "camera" | "backup" | "general";

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  cameraConfig,
  onSaveCameraConfig,
}) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>("camera");
  const [showCameraConfig, setShowCameraConfig] = useState(false);

  const handleCameraSave = (config: CameraConfig) => {
    onSaveCameraConfig(config);
    setShowCameraConfig(false);
  };

  const handleCameraCancel = () => {
    setShowCameraConfig(false);
  };

  const tabs = [
    { key: "camera" as SettingsTab, label: "Camera" },
    { key: "backup" as SettingsTab, label: "Backup & Restore" },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen && !showCameraConfig}
        onClose={onClose}
        title="Settings"
        size="large"
      >
        <div className="flex h-96">
          {/* Tabs */}
          <div className="w-1/3 border-r border-gray-200 pr-4">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium ${
                    activeTab === tab.key
                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 pl-4">
            {activeTab === "camera" && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Camera Configuration
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Configure which cameras are assigned to each slot in the
                  Multi-Camera Operator View.
                </p>

                <div className="space-y-3 overflow-scroll">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Current Configuration:
                    </h4>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div>
                        Slot 1:{" "}
                        {cameraConfig?.slot1?.deviceName || "Not configured"}
                      </div>
                      <div>
                        Slot 2:{" "}
                        {cameraConfig?.slot2?.deviceName || "Not configured"}
                      </div>
                      <div>
                        Slot 3:{" "}
                        {cameraConfig?.slot3?.deviceName || "Not configured"}
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="primary"
                    onClick={() => setShowCameraConfig(true)}
                    className="w-full"
                  >
                    Configure Cameras
                  </Button>
                </div>
              </div>
            )}

            {activeTab === "backup" && (
              <div className="h-full overflow-scroll">
                <BackupManagerPanel inModal={true} />
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Camera Configuration Modal */}
      <Modal
        isOpen={showCameraConfig}
        onClose={handleCameraCancel}
        title=""
        size="large"
      >
        <CameraConfigPanel
          cameraConfig={cameraConfig}
          onSave={handleCameraSave}
          onCancel={handleCameraCancel}
        />
      </Modal>
    </>
  );
};
